import type {
  ActionEnvelope,
  AgentSelection,
  ChatInputAnswer,
  ChatInputRequest,
  ChatState,
  MessageAttachment,
  ModelSelection,
  SessionState,
  ToolCallResult,
} from '@microsoft/agent-host-protocol'
import type {AcquireRelayLeaseOptions, RelayLeaseIdentity} from '@github-ui/ahp-relay/relay-registry'
import type {RelaySteeringMessage} from '@github-ui/ahp-relay/relay-steering-messages'
import type {
  RelayChatObserver,
  RelayModelCallFailure,
  RelayStatus,
  RelayTelemetryObserver,
} from '@github-ui/ahp-relay/relay-connection-runtime'
import type {ProvisioningStatus} from '@github-ui/ahp-relay/session-host/resolve-project'

export interface EstablishedAhpSessionIdentity extends RelayLeaseIdentity {
  environmentId: string
  sessionId: string
}

export interface PreparedPrompt {
  readonly text: string
  readonly attachments?: readonly MessageAttachment[]
  readonly model?: ModelSelection
  readonly agent?: AgentSelection
}

/** Creates the canonical prompt shape at a host boundary without carrying empty attachments. */
export function createPreparedPrompt(input: PreparedPrompt): PreparedPrompt {
  const attachments = input.attachments && input.attachments.length > 0 ? [...input.attachments] : undefined
  return {
    text: input.text,
    ...(attachments ? {attachments} : {}),
    ...(input.model ? {model: input.model} : {}),
    ...(input.agent ? {agent: input.agent} : {}),
  }
}

export interface EstablishedAhpPendingMessage {
  readonly id: string
  readonly prompt: PreparedPrompt
  readonly kind: 'queued' | 'steering'
}

export interface DispatchReceipt {
  readonly requestId: string
  readonly clientSeq: number
  readonly turnId?: string
  readonly pendingMessageId?: string
}

export type DirectTurnKind = 'new_chat' | 'existing_chat'
export type DirectTurnStartPath = 'cold' | 'resume' | 'active'

export interface DirectTurnSubmission {
  readonly requestId: string
  readonly turnKind: DirectTurnKind
  readonly startPath: DirectTurnStartPath
  readonly modelId?: string
}

/**
 * Optional browser-local observer for one product's direct-turn telemetry.
 * Queue and steering commands intentionally bypass this contract.
 */
export interface EstablishedAhpDirectTurnTelemetry {
  readinessChanged(ready: boolean): void
  beginSubmission(submission: DirectTurnSubmission): void
  readinessRequired(requestId: string): void
  readinessObserved(requestId: string): void
  turnDispatching(requestId: string): void
  turnDispatched(requestId: string, turnId: string): void
  preDispatchError(requestId: string): void
  acceptedAction(envelope: ActionEnvelope, previousState: ChatState | undefined, state: ChatState | undefined): void
  reset(): void
}

export interface StopOutcome {
  readonly accepted: boolean
  readonly turnId?: string
}

export interface EstablishedAhpSessionView {
  getSnapshot(): EstablishedAhpSessionViewSnapshot
  subscribe(listener: () => void): () => void
  observeChat(channel: string, observer: RelayChatObserver): () => void
  subscribeTelemetry?(observer: RelayTelemetryObserver): Promise<() => void>
}

export interface EstablishedAhpSessionViewSnapshot {
  readonly state: ChatState | undefined
  readonly status: RelayStatus | 'idle'
  readonly reachable: boolean
  readonly error: string | undefined
  readonly modelFailures: Record<string, RelayModelCallFailure> | undefined
  readonly provisioning: ProvisioningStatus | undefined
  readonly sessionState: SessionState | undefined
  readonly passive: boolean
  readonly passiveKnown: boolean
  readonly steeringMessages: readonly RelaySteeringMessage[]
}

export interface EstablishedAhpSessionInputController {
  submit(prompt: PreparedPrompt, requestId?: string): Promise<DispatchReceipt>
  queue(prompt: PreparedPrompt): Promise<DispatchReceipt>
  steer(prompt: PreparedPrompt, steeringId?: string): Promise<DispatchReceipt>
  sendNow(message: EstablishedAhpPendingMessage): Promise<DispatchReceipt>
  removeQueued(id: string): Promise<void>
}

export interface ToolConfirmationResponse {
  readonly approved: boolean
  readonly toolCallId: string
  readonly turnId: string
  readonly resultConfirmation?: boolean
  readonly selectedOptionId?: string
  readonly forSession?: boolean
}

export type EstablishedAhpPromptCommandType =
  | 'ask_user_response'
  | 'elicitation_response'
  | 'permission_response'
  | 'plan_approval_response'

export interface EstablishedAhpPrompt {
  readonly toolCallId: string
  readonly toolName: string
  readonly arguments: unknown
}

export interface EstablishedAhpToolConfirmationOption {
  readonly id: string
  readonly label: string
  readonly kind: 'approve' | 'deny'
  readonly selectedOptionId?: string
  readonly forSession?: boolean
}

export interface EstablishedAhpToolConfirmation {
  readonly turnId: string
  readonly toolCallId: string
  readonly title: string
  readonly detail?: string
  readonly options: readonly EstablishedAhpToolConfirmationOption[]
  readonly resultConfirmation: boolean
}

export interface InputCompletion {
  readonly requestId: string
  readonly response: 'accept' | 'decline' | 'cancel'
  readonly answers?: Record<string, ChatInputAnswer>
}

export interface EstablishedAhpSessionCommands {
  cancel(turnId?: string): StopOutcome
  respondToPrompt(input: {
    requestId: string
    response: 'accept' | 'decline' | 'cancel'
    answers?: InputCompletion['answers']
  }): void
  confirmTool(input: ToolConfirmationResponse): void
  completeTool(input: {turnId: string; toolCallId: string; result: ToolCallResult}): void
  renameSession(title: string): void
  setApproveAll(enabled: boolean): Promise<void>
}

export interface EstablishedAhpPlanningState {
  readonly status: 'idle' | 'pending' | 'approved' | 'rejected' | 'failed'
  readonly plan?: EstablishedAhpPlan
  readonly error?: string
}

export interface EstablishedAhpPlan {
  readonly id: string
  readonly title?: string
  readonly summary?: string
  readonly steps: readonly string[]
}

export interface EstablishedAhpPlanningAdapter {
  identify(request: ChatInputRequest): EstablishedAhpPlan | undefined
  approve(plan: EstablishedAhpPlan): Promise<void>
  reject(plan: EstablishedAhpPlan): Promise<void>
  revise(plan: EstablishedAhpPlan, prompt: PreparedPrompt): Promise<void>
  continue(plan: EstablishedAhpPlan): Promise<void>
}

export interface EstablishedAhpPlanningPolicy {
  readonly enabled: boolean
  readonly adapter?: EstablishedAhpPlanningAdapter
}

export interface EstablishedAhpSessionPlanning {
  readonly state: EstablishedAhpPlanningState
  readonly actions: {
    approve(): Promise<void>
    reject(): Promise<void>
    revise(prompt: PreparedPrompt): Promise<void>
    continue(): Promise<void>
  }
}

export type EstablishedAhpLifecycleState = 'connecting' | 'waking' | 'live' | 'reconnecting' | 'failed' | 'disposed'

export interface EstablishedAhpSessionLifecycle {
  getState(): EstablishedAhpLifecycleState
  subscribe(listener: () => void): () => void
}

export interface EstablishedAhpSession {
  readonly identity: EstablishedAhpSessionIdentity
  readonly view: EstablishedAhpSessionView
  readonly commands: EstablishedAhpSessionCommands
  readonly input: EstablishedAhpSessionInputController
  readonly planning?: EstablishedAhpSessionPlanning
  readonly lifecycle: EstablishedAhpSessionLifecycle
}

export interface EstablishedAhpSessionIntegrationServices {
  readonly references?: {
    resolve?: (reference: unknown) => Promise<readonly MessageAttachment[]> | readonly MessageAttachment[]
  }
  readonly attachments?: {
    prepare?: (attachment: unknown) => Promise<readonly MessageAttachment[]> | readonly MessageAttachment[]
  }
  readonly repository?: {
    getCurrent?: () => unknown
    select?: (repository: unknown) => void
  }
  readonly spaces?: {
    select?: (space: unknown) => void
  }
}

export interface EstablishedAhpSessionIntegrations extends EstablishedAhpSessionIntegrationServices {
  readonly view: EstablishedAhpSessionView
  readonly input: EstablishedAhpSessionInputController
  readonly commands: EstablishedAhpSessionCommands
  readonly lifecycle: EstablishedAhpSessionLifecycle
  readonly planning?: EstablishedAhpSessionPlanning
}

export interface FirstTurnHandoff {
  readonly requestId: string
  readonly prompt: PreparedPrompt
  readonly origin: 'new' | 'recovery'
}

export interface ProvisionRequest {
  readonly firstTurn?: FirstTurnHandoff
  readonly metadata?: Readonly<Record<string, unknown>>
}

export type EstablishRequest =
  | {readonly mode: 'provision'; readonly input: ProvisionRequest}
  | {readonly mode: 'attach'; readonly identity: EstablishedAhpSessionIdentity}
  | {readonly mode: 'wake'; readonly identity: EstablishedAhpSessionIdentity}
  | {
      readonly mode: 'recover'
      readonly identity: EstablishedAhpSessionIdentity
      readonly firstTurn?: FirstTurnHandoff
    }

export interface EstablishedAhpSessionProvisioned {
  readonly identity: EstablishedAhpSessionIdentity
  readonly relay: AcquireRelayLeaseOptions
  readonly directTurnTelemetry?: EstablishedAhpDirectTurnTelemetry
}

export interface EstablishedAhpSessionBootstrapAdapter {
  provision(input: ProvisionRequest): Promise<EstablishedAhpSessionProvisioned>
  attach(identity: EstablishedAhpSessionIdentity): Promise<EstablishedAhpSessionProvisioned>
  wake(identity: EstablishedAhpSessionIdentity): Promise<EstablishedAhpSessionProvisioned>
  recover(identity: EstablishedAhpSessionIdentity): Promise<EstablishedAhpSessionProvisioned>
  reset?(): void
  dispose?(): void
}

export type EstablishedAhpBootstrapState =
  | 'unbound'
  | 'provisioning'
  | 'history'
  | 'connecting'
  | 'waking'
  | 'reconnecting'
  | 'failed'
  | 'established'
  | 'disposed'

export interface EstablishedAhpSessionBootstrap {
  readonly state: EstablishedAhpBootstrapState
  readonly session: EstablishedAhpSession | undefined
  readonly directTurnTelemetry?: EstablishedAhpDirectTurnTelemetry
  /**
   * Installs host-owned relay diagnostics before a new connection is created.
   * Existing relay entries retain the callbacks from their original acquire.
   */
  configureRelayDiagnostics?(diagnostics: EstablishedAhpSessionRelayDiagnostics): void
  establish(request: EstablishRequest): Promise<EstablishedAhpSession>
  reset(): void
  dispose(): void
}

export type EstablishedAhpSessionRelayDiagnostics = Pick<AcquireRelayLeaseOptions, 'webSocketFactory' | 'onStatus'>

export class EstablishedAhpSessionError extends Error {
  readonly operation: string
  readonly phase: EstablishedAhpBootstrapState | EstablishedAhpLifecycleState
  readonly recoverable: boolean

  constructor(
    operation: string,
    phase: EstablishedAhpBootstrapState | EstablishedAhpLifecycleState,
    message: string,
    recoverable: boolean,
    options?: ErrorOptions,
  ) {
    super(message, options)
    this.name = 'EstablishedAhpSessionError'
    this.operation = operation
    this.phase = phase
    this.recoverable = recoverable
  }
}
